import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, Send, Sparkles } from "lucide-react";
import type { ChatMessage } from "@/types";
import { trackEvent } from "@/lib/audit";
import { bumpInsight, logChatMessage } from "@/lib/session";
import { useLanguage } from "@/hooks/useLanguage";
import { useMode } from "@/lib/mode";
import { evaluateScope } from "@/lib/ai/aiScopeGuard";
import { detectOracleIntent } from "@/lib/ai/sportsOracle";
import { useSafeInputHandlers } from "@/lib/mobileFloatingControls";

interface ChatPanelProps {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  onAiReply?: (content: string) => void;
  matchContext?: string;
  channel?: "match_ai" | "global_oracle";
  maxWords?: number;
  placeholder?: string;
  prefillText?: string;
  onPrefillConsumed?: () => void;
}

export function ChatPanel({ messages, onSend, onAiReply, matchContext, channel = "match_ai", maxWords, placeholder, prefillText, onPrefillConsumed }: ChatPanelProps) {
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserId = useRef<string | null>(null);
  const { lang, t } = useLanguage();
  const [mode] = useMode();
  const safe = useSafeInputHandlers();
  // Reading discipline state — pauses autoscroll while user reads/types
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const lastSeenCountRef = useRef(messages.length);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setIsAtBottom(true);
    setPendingCount(0);
    lastSeenCountRef.current = messages.length;
  }, [messages.length]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < 48;
    setIsAtBottom(atBottom);
    if (atBottom) {
      setPendingCount(0);
      lastSeenCountRef.current = messages.length;
    }
  }, [messages.length]);

  useEffect(() => {
    void trackEvent("ai_language_applied", { lang });
  }, [lang]);

  useEffect(() => {
    if (prefillText) {
      setInput(prefillText);
      onPrefillConsumed?.();
    }
  }, [prefillText, onPrefillConsumed]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    // Autoscroll discipline:
    // - Own user message → always follow (they just sent it).
    // - Otherwise → only follow if already pinned to bottom AND not typing.
    const isOwnUser = last && last.role === "user" && last.id !== lastUserId.current;
    if (isOwnUser || (isAtBottom && !inputFocused)) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
      lastSeenCountRef.current = messages.length;
      setPendingCount(0);
    } else {
      // Buffer silently — surface as a "new signals" badge.
      const delta = messages.length - lastSeenCountRef.current;
      if (delta > 0) setPendingCount(delta);
    }
    if (last && last.role === "user" && last.id !== lastUserId.current) {
      lastUserId.current = last.id;
      setTyping(true);
      (async () => {
        try {
          // 1) Scope guard — only for Match AI. Global Oracle accepts any
          //    soccer-wide question.
          if (channel === "match_ai") {
            const scope = evaluateScope(last.content, lang as "ES" | "EN" | "PT");
            if (!scope.inScope && scope.fallbackReply) {
              onAiReply?.(scope.fallbackReply);
              void logChatMessage("ai", scope.fallbackReply, "scope_guard");
              setTyping(false);
              return;
            }
          }
          // 2) Oracle intent hint — añade contexto cheap al prompt.
          const oracleIntent = detectOracleIntent(last.content);
          const matchCtxWithIntent = oracleIntent
            ? `${matchContext ?? ""}\nOracleIntent:${oracleIntent}`
            : matchContext;
          const res = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              matchContext: matchCtxWithIntent,
              lang,
              mode,
              channel,
              maxWords,
              messages: messages.map((m) => ({ role: m.role, content: m.content })),
            }),
          });
          const data = (await res.json()) as { content?: string; error?: string };
          if (!res.ok || !data.content) {
            const fallback =
              data.error === "rate_limited"
                ? t("ai_rate_limit")
                : data.error === "credits_exhausted"
                  ? t("ai_credits")
                  : t("ai_no_reply");
            onAiReply?.(fallback);
          } else {
            trackEvent("chat_ai_response", { len: data.content.length });
            void logChatMessage("ai", data.content, "google/gemini-3-flash-preview");
            onAiReply?.(data.content);
          }
        } catch {
          onAiReply?.(t("ai_network_error"));
        } finally {
          setTyping(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    onSend(text);
    trackEvent("chat_message", { content: text });
    void bumpInsight("chat_messages_sent");
    void logChatMessage("user", text);
    setInput("");
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl glass">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)]">
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div>
          <div className="text-xs font-bold">Sphere Analyst</div>
          <div className="text-[10px] text-muted-foreground">{t("ai_subtitle")}</div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {isAtBottom && !inputFocused ? (
            <>
              <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.24_145)] animate-pulse" />
              <span className="text-[10px] text-muted-foreground">{t("ai_online")}</span>
            </>
          ) : (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
              {t("chat_reading_mode")}
            </span>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="relative flex-1 space-y-4 overflow-y-auto px-4 py-5"
      >
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex gap-2 animate-slide-up ${m.role === "user" ? "flex-row-reverse" : ""}`}
          >
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                m.role === "ai"
                  ? "bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] text-white"
                  : "bg-accent text-foreground"
              }`}
            >
              {m.role === "ai" ? t("chat_avatar_ai") : t("chat_avatar_user")}
            </div>
            <div
              className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-[1.55] ${
                m.role === "ai"
                  ? "bg-secondary/70 text-foreground"
                  : "bg-primary text-primary-foreground"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] text-[10px] font-bold text-white">
              {t("chat_avatar_ai")}
            </div>
            <div className="rounded-2xl bg-secondary/70 px-4 py-3">
              <div className="flex items-end gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="block h-1.5 w-1.5 rounded-full bg-muted-foreground"
                    style={{ animation: `typing 1s ease-in-out ${i * 0.15}s infinite` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {(!isAtBottom || pendingCount > 0) && (
        <button
          type="button"
          onClick={() => scrollToBottom("smooth")}
          className="pointer-events-auto absolute left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:opacity-90"
          style={{ bottom: "5.25rem" }}
          aria-label={t("chat_back_to_live")}
        >
          <ArrowDown className="h-3 w-3" />
          {pendingCount > 0
            ? (pendingCount === 1
                ? t("chat_new_signals_one")
                : t("chat_new_signals_many").replace("{n}", String(pendingCount)))
            : t("chat_back_to_live")}
        </button>
      )}

      <form
        onSubmit={submit}
        className="flex items-center gap-2 border-t border-border/60 px-3 py-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => { setInputFocused(true); safe.onFocus?.(); }}
          onBlur={() => { setInputFocused(false); safe.onBlur?.(); }}
          placeholder={placeholder ?? t("ai_placeholder")}
          aria-label="Chat input"
          className="flex-1 min-w-0 rounded-xl bg-accent/40 px-3 py-3 text-base sm:text-sm outline-none placeholder:text-muted-foreground/70 focus:bg-accent"
          style={{ marginBottom: 0 }}
        />
        <button
          type="submit"
          aria-label="Send"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
          disabled={!input.trim()}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}